import { ThemedText } from "@/components/themed-text";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { PriceEstimate } from "@/lib/services/pricing";
import { money } from "@/lib/utils";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { formatETA } from "@/lib/utils/map-utils";
import Ionicons from "@react-native-vector-icons/ionicons";
import { Image } from "expo-image";
import React from "react";
import {
  ActivityIndicator,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";

type TripEstimateViewProps = {
  loading: boolean;
  estimate: PriceEstimate | null;
  locationOnly: boolean;
  fromAddress?: string | null;
  toAddress?: string | null;
  onRetry: () => void;
  addon?: React.ReactNode;
};

export function TripEstimateView({
  addon,
  ...props
}: TripEstimateViewProps) {
  const desktop = useDesktopLayout();

  if (desktop) {
    return (
      <View style={styles.desktopRow}>
        {addon ? <View style={styles.desktopColumn}>{addon}</View> : null}
        <View style={styles.desktopColumn}>
          <TripEstimateContent {...props} desktop />
        </View>
      </View>
    );
  }

  return (
    <>
      {addon}
      <TripEstimateContent {...props} desktop={false} />
    </>
  );
}

function TripEstimateContent({
  loading,
  estimate,
  locationOnly,
  fromAddress,
  toAddress,
  onRetry,
  desktop,
}: Omit<TripEstimateViewProps, "addon"> & { desktop: boolean }) {
  const { theme } = useAuth();
  const desktopCardStyle = desktop
    ? [
        styles.desktopCard,
        { backgroundColor: theme.card, borderColor: theme.border },
      ]
    : null;
  const desktopStatBoxStyle = desktop
    ? [styles.desktopStatBox, { borderColor: theme.border }]
    : null;
  const statValueStyle = [
    styles.statValue,
    desktop ? { color: theme.text } : null,
  ];

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={BrandColors.primary} />
        <ThemedText style={styles.loadingText} type="small">
          Calculating price...
        </ThemedText>
      </View>
    );
  }

  if (!estimate) {
    return (
      <View style={styles.errorContainer}>
        <Ionicons
          name="alert-circle-outline"
          size={48}
          color={BrandColors.error}
        />
        <ThemedText style={styles.errorText} type="small">
          Could not calculate estimate
        </ThemedText>
        <TouchableOpacity
          style={styles.retryButton}
          onPress={onRetry}
          activeOpacity={0.8}
        >
          <Ionicons name="close" size={16} color={BrandColors.error} />
          <ThemedText style={styles.retryText} type="smallSemiBold">
            Try Again
          </ThemedText>
        </TouchableOpacity>
      </View>
    );
  }

  const marketplaceMeta =
    estimate.engine_label || estimate.is_operator_exchange ? (
      <View style={styles.marketplaceMeta}>
        {estimate.is_operator_exchange ? (
          <ThemedText style={styles.marketplaceBadge} type="extraSmallSemiBold">
            Operator Exchange
          </ThemedText>
        ) : null}
        {estimate.engine_label ? (
          <ThemedText style={styles.marketplaceEngine} type="extraSmall">
            {estimate.engine_label}
            {estimate.make_offer_allowed
              ? " · Make Offer available"
              : " · Fixed quote"}
          </ThemedText>
        ) : null}
      </View>
    ) : null;

  return (
    <>
      <View
        style={[
          styles.tripSummaryCard,
          { backgroundColor: theme.background, shadowColor: theme.text },
          desktopCardStyle,
        ]}
      >
        <View style={styles.tripSummaryHeader}>
          <Ionicons
            name="calendar-outline"
            size={18}
            color={BrandColors.primary}
          />
          <ThemedText style={styles.tripSummaryTitle} type="defaultSemiBold">
            Trip Summary
          </ThemedText>
        </View>

        <View style={styles.routeSection}>
          <View style={styles.routeIconColumn}>
            <View style={styles.pickupIconOuter}>
              <View style={styles.pickupIconInner} />
            </View>
            {!locationOnly ? (
              <View style={styles.routeDashedLine}>
                {Array.from({ length: 5 }).map((_, index) => (
                  <View key={index} style={styles.routeDash} />
                ))}
              </View>
            ) : null}
            {!locationOnly ? (
              <Ionicons name="location" size={20} color="#E11D48" />
            ) : null}
          </View>

          <View style={styles.routeDetails}>
            <View style={styles.routeStop}>
              <ThemedText style={styles.pickupLabel} type="extraSmallSemiBold">
                {locationOnly ? "Vehicle Location" : "Pick Up"}
              </ThemedText>
              <ThemedText
                style={styles.routeAddress}
                type="smallSemiBold"
                numberOfLines={2}
              >
                {fromAddress}
              </ThemedText>
            </View>

            {!locationOnly ? (
              <View style={styles.routeStop}>
                <ThemedText
                  style={styles.deliveryLabel}
                  type="extraSmallSemiBold"
                >
                  Delivery
                </ThemedText>
                <ThemedText
                  style={styles.routeAddress}
                  type="smallSemiBold"
                  numberOfLines={2}
                >
                  {toAddress}
                </ThemedText>
              </View>
            ) : null}
          </View>
        </View>

        {!locationOnly ? (
          <View style={styles.statsRow}>
            <View style={[styles.statBox, desktopStatBoxStyle]}>
              <Ionicons
                name="resize-outline"
                size={16}
                color={BrandColors.primary}
              />
              <View style={styles.statBoxText}>
                <ThemedText style={styles.statLabel} type="extraSmall">
                  Distance
                </ThemedText>
                <ThemedText
                  style={statValueStyle}
                  type="smallSemiBold"
                  numberOfLines={2}
                >
                  {estimate.distance_miles} miles
                </ThemedText>
              </View>
            </View>
            <View style={[styles.statBox, desktopStatBoxStyle]}>
              <Ionicons
                name="time-outline"
                size={16}
                color={BrandColors.primary}
              />
              <View style={styles.statBoxText}>
                <ThemedText
                  style={styles.statLabel}
                  type="extraSmall"
                  numberOfLines={2}
                >
                  Estimated Duration
                </ThemedText>
                <ThemedText
                  style={statValueStyle}
                  type="smallSemiBold"
                  numberOfLines={2}
                >
                  {formatETA(estimate.estimated_duration_minutes)}
                </ThemedText>
              </View>
            </View>
          </View>
        ) : null}

        {!desktop ? (
          <>
            <View
              style={[styles.divider, { backgroundColor: theme.border }]}
            />

            <View style={styles.priceRow}>
              <ThemedText style={styles.priceLabel} type="defaultSemiBold">
                Estimated Price
              </ThemedText>
              <ThemedText style={styles.priceValue} type="title">
                {money(estimate.total)}
              </ThemedText>
            </View>

            {marketplaceMeta}
          </>
        ) : null}
      </View>

      {desktop ? (
        <View style={[styles.desktopPriceCard, desktopCardStyle]}>
          <View style={styles.desktopPriceText}>
            <ThemedText style={styles.priceLabel} type="defaultSemiBold">
              Estimated Price
            </ThemedText>
            {marketplaceMeta}
          </View>
          <ThemedText style={styles.priceValue} type="title">
            {money(estimate.total)}
          </ThemedText>
        </View>
      ) : null}

      <View
        style={[
          styles.driversCard,
          { backgroundColor: theme.background, shadowColor: "#fff" },
          desktopCardStyle,
        ]}
      >
        <View style={styles.driversContent}>
          <View style={styles.driversIconBox}>
            <Ionicons name="flash" size={18} color={BrandColors.primary} />
          </View>
          <View style={styles.driversTextContent}>
            <ThemedText style={styles.driversText} type="smallSemiBold">
              92% of our drivers arrive between 30 - 40 minutes
            </ThemedText>
            <ThemedText style={styles.driversSubtext} type="extraSmall">
              Delivery times are always estimate and may vary depending on other
              factors
            </ThemedText>
          </View>
        </View>
        <Image
          source={require("@/assets/images/trip-estimate-truck.png")}
          style={styles.driversTruckImage}
          contentFit="contain"
        />
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  desktopRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
  },
  desktopColumn: {
    flex: 1,
    gap: 16,
  },
  desktopCard: {
    borderWidth: 1,
    marginBottom: 0,
    shadowOpacity: 0,
    elevation: 0,
  },
  desktopStatBox: {
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  desktopPriceCard: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    borderRadius: 20,
    padding: 20,
  },
  desktopPriceText: {
    flex: 1,
    gap: 4,
  },
  loadingContainer: {
    padding: 40,
    alignItems: "center",
    gap: 16,
  },
  loadingText: {
    color: "#94a3b8",
    fontSize: 16,
  },
  tripSummaryCard: {
    borderRadius: 20,
    padding: 20,
    gap: 16,
    marginBottom: 16,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.05,
    shadowRadius: 12,
    elevation: 4,
  },
  tripSummaryHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  tripSummaryTitle: {
    fontSize: 16,
    fontWeight: "700",
  },
  routeSection: {
    flexDirection: "row",
    gap: 12,
  },
  routeIconColumn: {
    alignItems: "center",
    paddingTop: 2,
  },
  pickupIconOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: BrandColors.primary,
    alignItems: "center",
    justifyContent: "center",
  },
  pickupIconInner: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: BrandColors.white,
  },
  routeDashedLine: {
    alignItems: "center",
    gap: 3,
    marginVertical: 6,
  },
  routeDash: {
    width: 2,
    height: 5,
    borderRadius: 1,
    backgroundColor: BrandColors.lightGray,
  },
  routeDetails: {
    flex: 1,
    gap: 20,
  },
  routeStop: {
    gap: 4,
  },
  pickupLabel: {
    color: BrandColors.primary,
    fontSize: 12,
    fontWeight: "600",
  },
  deliveryLabel: {
    color: "#E11D48",
    fontSize: 12,
    fontWeight: "600",
  },
  routeAddress: {
    fontSize: 15,
    fontWeight: "700",
    lineHeight: 20,
  },
  statsRow: {
    flexDirection: "row",
    gap: 12,
  },
  statBox: {
    flex: 1,
    flexDirection: "row",
    alignItems: "flex-start",
    backgroundColor: BrandColors.primaryLight,
    borderRadius: 12,
    padding: 12,
    gap: 8,
    minWidth: 0,
  },
  statBoxText: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  statLabel: {
    color: BrandColors.mediumGray,
    fontSize: 11,
    flexShrink: 1,
  },
  statValue: {
    fontSize: 14,
    color: BrandColors.charcoal,
    flexShrink: 1,
  },
  divider: {
    height: 1,
  },
  priceRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  priceLabel: {
    fontSize: 16,
    fontWeight: "700",
  },
  priceValue: {
    color: BrandColors.primary,
    fontSize: 22,
    fontWeight: "800",
  },
  marketplaceMeta: {
    gap: 4,
  },
  marketplaceBadge: {
    color: BrandColors.primary,
    fontSize: 12,
  },
  marketplaceEngine: {
    color: BrandColors.mediumGray,
    fontSize: 12,
  },
  driversCard: {
    borderRadius: 20,
    padding: 16,
    minHeight: 120,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.05,
    shadowRadius: 12,
    elevation: 4,
  },
  driversContent: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    paddingRight: 110,
  },
  driversIconBox: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: BrandColors.primaryLight,
    alignItems: "center",
    justifyContent: "center",
  },
  driversTextContent: {
    flex: 1,
    gap: 6,
  },
  driversText: {
    fontSize: 14,
    fontWeight: "700",
    lineHeight: 20,
  },
  driversSubtext: {
    color: BrandColors.mediumGray,
    lineHeight: 16,
  },
  driversTruckImage: {
    position: "absolute",
    right: 8,
    bottom: 18,
    width: 120,
    height: 90,
  },
  errorContainer: {
    padding: 40,
    alignItems: "center",
    gap: 16,
  },
  errorText: {
    color: BrandColors.error,
    fontSize: 16,
  },
  retryButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    minWidth: 120,
    height: 50,
    paddingHorizontal: 16,
    backgroundColor: BrandColors.errorLight,
    borderRadius: 12,
  },
  retryText: {
    color: BrandColors.error,
    fontWeight: "600",
  },
});
